import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useState,
} from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    type CaregiverShiftIds,
    withoutCaregiversInSelection,
} from '@apps/web/lib/caregiver_shift_selections';
import { parseKnownApiError } from '@apps/web/lib/known_api_errors';

type SkipListRefusal<TRetry> = {
    readonly caregiverIds: ReadonlyArray<string>;
    readonly caregiverNames: ReadonlyArray<string>;
    readonly caregiverShiftIds: CaregiverShiftIds;
    readonly retry: TRetry;
};

export function useSkipListRetry<TRetry>({
    setSelectedCaregiverIdsByShift,
    resubmit,
}: {
    readonly setSelectedCaregiverIdsByShift: Dispatch<
        SetStateAction<Record<string, Array<string>>>
    >;
    readonly resubmit: (
        caregiverShiftIds: CaregiverShiftIds,
        retry: TRetry,
    ) => Promise<unknown>;
}) {
    const [refusal, setRefusal] = useState<SkipListRefusal<TRetry> | null>(
        null,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSkipListRefusal = useCallback(
        (
            error: unknown,
            caregiverShiftIds: CaregiverShiftIds,
            retry: TRetry,
        ): boolean => {
            const knownError = parseKnownApiError(error);
            if (knownError?.kind !== 'caregivers_skip_listed') {
                return false;
            }
            setRefusal({
                caregiverIds: knownError.caregiverIds,
                caregiverNames: knownError.caregiverNames,
                caregiverShiftIds,
                retry,
            });
            return true;
        },
        [],
    );

    const confirm = () => {
        if (!refusal) {
            return;
        }
        const { caregiverIds } = refusal;
        setSelectedCaregiverIdsByShift((selected) =>
            withoutCaregiversInSelection(selected, caregiverIds),
        );
        setIsSubmitting(true);
        void resubmit(
            Object.fromEntries(
                Object.entries(refusal.caregiverShiftIds).filter(
                    ([caregiverId]) => !caregiverIds.includes(caregiverId),
                ),
            ),
            refusal.retry,
        ).finally(() => {
            setIsSubmitting(false);
            setRefusal((current) => (current === refusal ? null : current));
        });
    };

    return {
        handleSkipListRefusal,
        skipListDialog: (
            <OutreachSkipListDialog
                caregivers={
                    refusal?.caregiverIds.map((id, index) => ({
                        id,
                        name: refusal.caregiverNames[index] ?? id,
                    })) ?? null
                }
                isSubmitting={isSubmitting}
                onCancel={() => setRefusal(null)}
                onConfirm={confirm}
            />
        ),
    };
}

function OutreachSkipListDialog({
    caregivers,
    isSubmitting,
    onCancel,
    onConfirm,
}: {
    readonly caregivers: ReadonlyArray<{
        readonly id: string;
        readonly name: string;
    }> | null;
    readonly isSubmitting: boolean;
    readonly onCancel: () => void;
    readonly onConfirm: () => void;
}) {
    return (
        <Dialog
            open={caregivers !== null}
            onOpenChange={(open) => {
                if (!open) {
                    onCancel();
                }
            }}
        >
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        Some caregivers are on your skip list
                    </DialogTitle>
                </DialogHeader>
                <ul className="list-disc pl-5 text-sm">
                    {caregivers?.map((caregiver) => (
                        <li key={caregiver.id}>{caregiver.name}</li>
                    ))}
                </ul>
                <DialogDescription>
                    They can’t be contacted from this outreach.
                </DialogDescription>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        disabled={isSubmitting}
                        onClick={onConfirm}
                    >
                        {isSubmitting ? 'Starting…' : 'Start without them'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
